/**
 * webrtcDiagnostics — deterministic, opt-in evidence for the consultation media
 * path.
 *
 * Why this exists
 * ---------------
 * The consultation screen could only ever say "Connecting…". A Permissions-Policy
 * block, a denied camera, a signaling failure, a NAT-traversal failure and a
 * peer-connection failure all looked identical, so the two-device production
 * test could not identify the failed stage — and neither could support.
 *
 * This module records the stage-by-stage timeline (socket → join → offer/answer
 * → ICE candidates → ICE connectivity → remote media) plus the real
 * `RTCPeerConnection.getStats()` evidence (selected candidate pair, bytes,
 * packets and decoded frames). It is EVIDENCE ONLY: it never changes what the
 * call does, and it is disabled unless explicitly switched on.
 *
 * Safety contract
 * ---------------
 *   - disabled by default; enabled only by `?rtcdebug=1` or
 *     `localStorage.bhaby_rtc_debug = '1'` (a developer/support action);
 *   - never records JWTs, tokens, SDP bodies, media, or patient data;
 *   - candidate addresses are reduced to a type (`host`/`srflx`/`prflx`/`relay`)
 *     and protocol — an address never leaves the device;
 *   - every recorded value is a JSON primitive and is truncated, so a summary is
 *     always safe to paste into a support report.
 */

export type CandidateType = 'host' | 'srflx' | 'prflx' | 'relay' | 'unknown';

/**
 * Reduce an ICE candidate / getStats candidate line to its transport type.
 * `candidate:… typ srflx raddr 10.0.0.1 …` → `srflx`.
 * Addresses are intentionally NOT parsed out — only the type is evidence.
 */
export function candidateTypeFromString(candidate: string | null | undefined): CandidateType {
  if (typeof candidate !== 'string' || candidate.length === 0) return 'unknown';
  const match = /(?:^|\s)typ\s+(host|srflx|prflx|relay)(?:\s|$)/.exec(candidate);
  return (match?.[1] as CandidateType) ?? 'unknown';
}

/**
 * Normalise a candidate type from EITHER source.
 *
 * `getStats()` reports `candidateType` as a bare value (`'host'`, `'srflx'`,
 * `'relay'`), while an ICE candidate is a raw SDP line (`candidate:… typ host …`).
 * Passing the bare value to the line parser silently yields `unknown`, which is
 * why the very first real two-device report showed `pair: 'unknown->unknown'`
 * for a call that was demonstrably connected — the one field that answers "was
 * a relay needed?" was blanked out.
 */
export function normaliseCandidateType(value: unknown): CandidateType {
  const fromLine = candidateTypeFromString(typeof value === 'string' ? value : '');
  if (fromLine !== 'unknown') return fromLine;
  const bare = String(value ?? '').trim().toLowerCase();
  return bare === 'host' || bare === 'srflx' || bare === 'prflx' || bare === 'relay'
    ? (bare as CandidateType)
    : 'unknown';
}

/** `udp`/`tcp` from a candidate line, or null when it cannot be determined. */
export function candidateProtocolFromString(candidate: string | null | undefined): string | null {
  if (typeof candidate !== 'string') return null;
  const match = /(?:^|\s)(udp|tcp)(?:\s|$)/i.exec(candidate);
  return match ? match[1].toLowerCase() : null;
}

/** Local storage key / URL flag that switches diagnostics on. */
export const RTC_DEBUG_STORAGE_KEY = 'bhaby_rtc_debug';
export const RTC_DEBUG_QUERY_FLAG = 'rtcdebug';

/**
 * Is diagnostics capture enabled for this browser?
 * Guarded for non-browser (test/SSR) environments.
 */
export function isRtcDiagnosticsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get(RTC_DEBUG_QUERY_FLAG) === '1') return true;
    return window.localStorage.getItem(RTC_DEBUG_STORAGE_KEY) === '1';
  } catch {
    // Private mode / blocked storage: stay off rather than throw.
    return false;
  }
}

export interface RtcDiagEntry {
  /** Milliseconds since the collector was created. */
  t: number;
  /** Stage name, e.g. `socket.connected`, `ice.candidate.sent`. */
  event: string;
  /** Flat, sanitised, JSON-safe detail. */
  detail?: Record<string, string | number | boolean | null>;
}

const MAX_DETAIL_VALUE_LENGTH = 120;
const DEFAULT_RING_SIZE = 120;

/** Keep a value JSON-safe and short; anything else becomes a type tag. */
function sanitiseValue(value: unknown): string | number | boolean | null {
  if (value === null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return value.length > MAX_DETAIL_VALUE_LENGTH ? `${value.slice(0, MAX_DETAIL_VALUE_LENGTH)}…` : value;
  }
  if (Array.isArray(value)) return `[array:${value.length}]`;
  if (typeof value === 'undefined') return 'undefined';
  return typeof value;
}

function sanitiseDetail(detail?: Record<string, unknown>): Record<string, string | number | boolean | null> | undefined {
  if (!detail) return undefined;
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(detail)) out[key] = sanitiseValue(value);
  return out;
}

export interface RtcDiagnostics {
  readonly enabled: boolean;
  record(event: string, detail?: Record<string, unknown>): void;
  entries(): RtcDiagEntry[];
  /** Tally an ICE candidate by transport type (evidence of NAT traversal). */
  countCandidate(type: CandidateType): void;
  /** Store the latest `getStats()` media evidence. */
  setMedia(value: Record<string, unknown>): void;
  media(): Record<string, unknown>;
  /** Plain-text, paste-safe evidence report. */
  summary(): string;
  /** The diagnostic JSON attached to `window.__bhabyRtcDiag`. */
  toJSON(): Record<string, unknown>;
}

/**
 * Create the in-memory collector. A disabled collector still exposes the same
 * shape (so call sites stay branch-free) but records nothing and prints nothing.
 */
export function createRtcDiagnostics(enabled: boolean, ringSize = DEFAULT_RING_SIZE): RtcDiagnostics {
  const startedAt = Date.now();
  const buffer: RtcDiagEntry[] = [];
  const counts: Record<string, number> = {};
  const candidateTypes: Record<CandidateType, number> = { host: 0, srflx: 0, prflx: 0, relay: 0, unknown: 0 };
  let media: Record<string, unknown> = {};

  const record: RtcDiagnostics['record'] = (event, detail) => {
    // A disabled collector must have NO footprint: no counters, no buffer and no
    // console output, so a normal consultation pays nothing and leaks nothing.
    if (!enabled) return;
    counts[event] = (counts[event] ?? 0) + 1;
    const entry: RtcDiagEntry = { t: Date.now() - startedAt, event };
    const safe = sanitiseDetail(detail);
    if (safe) entry.detail = safe;
    buffer.push(entry);
    if (buffer.length > ringSize) buffer.shift();
    // Console output is the primary capture path for a two-device test.
    // eslint-disable-next-line no-console
    console.info(`[rtc] +${entry.t}ms ${event}`, safe ?? '');
  };

  return {
    enabled,
    record,
    entries: () => buffer.slice(),
    countCandidate: (type) => { if (enabled) candidateTypes[type] += 1; },
    setMedia: (value) => { if (enabled) media = value; },
    media: () => media,
    toJSON: () => ({
      enabled,
      stages: counts,
      candidateTypes: { ...candidateTypes },
      media,
      timeline: buffer.slice(),
    }),
    summary: () => {
      const lines: string[] = [];
      lines.push(`BHABY RTC diagnostics (${enabled ? 'enabled' : 'disabled'})`);
      lines.push(`stages: ${Object.entries(counts).map(([k, v]) => `${k}=${v}`).join(' ') || 'none'}`);
      const types = (Object.entries(candidateTypes) as Array<[CandidateType, number]>)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}=${v}`);
      lines.push(`ice candidates: ${types.join(' ') || 'none'}`);
      if (Object.keys(media).length > 0) lines.push(`media: ${JSON.stringify(media)}`);
      for (const entry of buffer) {
        lines.push(`+${entry.t}ms ${entry.event}${entry.detail ? ` ${JSON.stringify(entry.detail)}` : ''}`);
      }
      return lines.join('\n');
    },
  };
}

export interface RemoteMediaEvidence {
  audio: { bytesReceived: number | null; packetsReceived: number | null; packetsLost: number | null } | null;
  video: {
    bytesReceived: number | null;
    packetsReceived: number | null;
    packetsLost: number | null;
    framesDecoded: number | null;
  } | null;
  selectedPair: { localType: CandidateType; remoteType: CandidateType; protocol: string | null } | null;
}

const asNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * Extract the evidence that actually proves remote media end-to-end:
 * the nominated candidate pair (was a relay needed?) and, per media kind, the
 * received bytes/packets/frames.
 *
 * `null` fields mean "not reported yet" — never "working".
 */
export async function collectRemoteMediaEvidence(pc: RTCPeerConnection): Promise<RemoteMediaEvidence> {
  const evidence: RemoteMediaEvidence = { audio: null, video: null, selectedPair: null };
  let report: RTCStatsReport;
  try {
    report = await pc.getStats();
  } catch {
    return evidence;
  }

  // Phase 1: index every stat. `selectedCandidatePairId` points at a stat that
  // may not have been visited yet, so resolution must happen AFTER indexing —
  // resolving during the same pass silently produced nulls.
  const byId = new Map<string, any>();
  report.forEach((stat: any) => byId.set(stat.id, stat));

  // Phase 2: find the nominated pair, preferring the browser's own selection.
  let selectedPair: any = null;
  report.forEach((stat: any) => {
    if (stat.type === 'transport' && stat.selectedCandidatePairId) {
      selectedPair = byId.get(stat.selectedCandidatePairId) ?? selectedPair;
    }
    if (stat.type === 'candidate-pair' && (stat.selected || (stat.nominated && stat.state === 'succeeded'))) {
      selectedPair = stat;
    }
  });
  if (selectedPair) {
    const local = byId.get(selectedPair.localCandidateId);
    const remote = byId.get(selectedPair.remoteCandidateId);
    evidence.selectedPair = {
      localType: normaliseCandidateType(local?.candidateType ?? local?.candidate),
      remoteType: normaliseCandidateType(remote?.candidateType ?? remote?.candidate),
      protocol: (selectedPair.protocol ?? local?.protocol ?? remote?.protocol ?? null) as string | null,
    };
  }

  report.forEach((stat: any) => {
    if (stat.type !== 'inbound-rtp') return;
    if (stat.kind === 'audio' || stat.mediaType === 'audio') {
      evidence.audio = {
        bytesReceived: asNumber(stat.bytesReceived),
        packetsReceived: asNumber(stat.packetsReceived),
        packetsLost: asNumber(stat.packetsLost),
      };
    } else if (stat.kind === 'video' || stat.mediaType === 'video') {
      evidence.video = {
        bytesReceived: asNumber(stat.bytesReceived),
        packetsReceived: asNumber(stat.packetsReceived),
        packetsLost: asNumber(stat.packetsLost),
        framesDecoded: asNumber(stat.framesDecoded),
      };
    }
  });

  return evidence;
}

/** True when remote media is demonstrably flowing (not merely "connected"). */
export function hasReceivedRemoteMedia(evidence: RemoteMediaEvidence): boolean {
  const videoOk = Boolean(evidence.video && (evidence.video.bytesReceived ?? 0) > 0);
  const audioOk = Boolean(evidence.audio && (evidence.audio.bytesReceived ?? 0) > 0);
  return videoOk || audioOk;
}
